/**
 * FR5-AC-006 — client-side staleness cascade for storyboard scenes.
 *
 * Staleness is DERIVED, never stored: the plan's current prompts/duration are
 * compared against what actually rendered — the active take (motion prompt,
 * duration, keyframe experiment) and the keyframe slot's selected experiment
 * (image prompt). A render appends a take that mirrors the current setup, so
 * rendering clears exactly its own flags.
 *
 * Cascade: keyframe-prompt change → keyframe outdated → clip outdated;
 * motion/duration change → clip outdated only; needsReanchor (chain neighbor
 * changed) → clip outdated; thesis change → rewrite-eligible, NOT stale.
 */

export interface SceneLike {
  index: number;
  motionPrompt: string;
  keyframePrompt: string;
  durationSec: number;
  continuityMode?: string;
  keyframeSlotKey?: string;
  needsReanchor?: boolean;
  activeTakeId?: string;
  takes?: Array<{
    id: string;
    motionPrompt?: string;
    durationSec?: number;
    keyframeExperimentId?: string;
  }>;
}

export interface KeyframeSlotLike {
  selectedExperimentId?: string;
  experiments?: Record<string, { promptTemplate?: string; generatedUrl?: string; status?: string }>;
}

export interface SceneStaleness {
  /** Selected keyframe image no longer matches the scene's keyframe prompt. */
  keyframeOutdated: boolean;
  /** Rendered clip no longer matches the scene's current setup. */
  clipOutdated: boolean;
  /** Human-readable reasons, in cascade order. */
  reasons: string[];
}

export function deriveSceneStaleness(
  scene: SceneLike,
  keyframeSlot?: KeyframeSlotLike,
): SceneStaleness {
  const reasons: string[] = [];

  // Keyframe staleness: only scenes that OWN a keyframe can have one.
  let keyframeOutdated = false;
  if (scene.continuityMode !== 'chain' && keyframeSlot?.experiments) {
    const selectedId = keyframeSlot.selectedExperimentId ?? Object.keys(keyframeSlot.experiments)[0];
    const selected = selectedId ? keyframeSlot.experiments[selectedId] : undefined;
    if (selected?.promptTemplate && selected.promptTemplate !== scene.keyframePrompt) {
      keyframeOutdated = true;
      reasons.push('keyframe prompt changed → keyframe outdated');
    }
  }

  const activeTake = scene.activeTakeId
    ? (scene.takes ?? []).find((take) => take.id === scene.activeTakeId)
    : undefined;

  let clipOutdated = false;
  if (activeTake) {
    if (keyframeOutdated) {
      clipOutdated = true;
      reasons.push('keyframe outdated → clip outdated');
    }
    if (activeTake.motionPrompt !== undefined && activeTake.motionPrompt !== scene.motionPrompt) {
      clipOutdated = true;
      reasons.push('motion prompt changed → clip outdated');
    }
    if (activeTake.durationSec !== undefined && activeTake.durationSec !== scene.durationSec) {
      clipOutdated = true;
      reasons.push('duration changed → clip outdated');
    }
    // Keyframe swapped (setKeyframe / regenerate) after this take rendered.
    if (
      !keyframeOutdated
      && scene.continuityMode !== 'chain'
      && activeTake.keyframeExperimentId
      && keyframeSlot?.selectedExperimentId
      && activeTake.keyframeExperimentId !== keyframeSlot.selectedExperimentId
    ) {
      clipOutdated = true;
      reasons.push('first frame replaced → clip outdated');
    }
  }
  if (scene.needsReanchor) {
    clipOutdated = true;
    reasons.push('chain neighbor changed → first frame re-anchors on next render');
  }

  return { keyframeOutdated, clipOutdated, reasons };
}

/**
 * Pre-save cascade for the edit form: what WILL become stale if the drafts
 * are applied. Mirrors mock 12's banner (`prompt ✎ → keyframe outdated →
 * clip outdated`).
 */
export function deriveDraftCascade(input: {
  keyframePromptDirty: boolean;
  motionPromptDirty: boolean;
}): { keyframeOutdated: boolean; clipOutdated: boolean } {
  return {
    keyframeOutdated: input.keyframePromptDirty,
    clipOutdated: input.keyframePromptDirty || input.motionPromptDirty,
  };
}
